/**
 * The device's side of the cloud mirror — where `src/core/sync-engine.ts`'s
 * rules meet Dexie and the network.
 *
 * One pass is push-then-pull:
 *
 * 1. Everything written locally since the last push goes up to `/api/sync/push`,
 *    including the ledger rows, so "מי ביצע את השינוי" is answerable from any
 *    device and not just the one the change was made on.
 * 2. `/api/sync/pull` hands back everything stamped after the pull cursor, and
 *    each record is merged against the local one by `mergeRecord()`. Whatever
 *    lands is announced on the reactive store as a *remote* change, so the
 *    screens re-read another operator's write on the frame it arrives rather
 *    than whenever something else happens to make them reload.
 *
 * Nothing here is required for the application to work. A device with no
 * backend behind it — a dev server, a phone with no signal, a deployment whose
 * D1 binding is missing — gets a failed fetch, reports `offline`, and every
 * screen carries on reading Dexie exactly as before. That is the whole contract:
 * the cloud makes the data shared, it never makes it necessary.
 */
import { db } from './index';
import { readMeta, writeMeta } from './meta-store';
import { readAuthToken } from './cloud-auth-store';
import { APP_CONFIG } from '../core/config';
import {
  EMPTY_CURSOR,
  SYNC_TABLES,
  advanceCursor,
  pendingPush,
  pulledDomains,
  shouldApplyRemote,
  syncEndpoint,
  unseenAuditRows,
} from '../core/sync-engine';
import type { SyncCursor, SyncTable, SyncableRecord } from '../core/sync-engine';
import { emitRemoteStoreChange } from '../core/store-events';
import type { AuditLogEntry } from '../types/audit';

/** Where the device's position in the conversation is filed in `meta`. */
export const SYNC_CURSOR_KEY = 'sync-cursor';

/** How long a single pass may take before it is abandoned. */
export const SYNC_TIMEOUT_MS = 15_000;

/** What one pass moved. */
export interface SyncResult {
  ok: boolean;
  pushed: number;
  pulled: number;
  /** Ledger rows written by this pass. */
  auditPulled: number;
  /** Why the pass failed, when it did. */
  error?: string;
  /** True when something landed locally and the screens have to re-read. */
  changed: boolean;
}

const EMPTY_RESULT: SyncResult = { ok: true, pushed: 0, pulled: 0, auditPulled: 0, changed: false };

// ── Cursor ───────────────────────────────────────────────────────────

export async function readCursor(): Promise<SyncCursor> {
  const stored = await readMeta<Partial<SyncCursor>>(SYNC_CURSOR_KEY);
  return { ...EMPTY_CURSOR, ...(stored ?? {}) };
}

export async function writeCursor(cursor: SyncCursor): Promise<void> {
  await writeMeta(SYNC_CURSOR_KEY, cursor);
}

/** Forgets where the device had got to — the next pass re-reads everything. */
export async function resetSyncCursor(): Promise<void> {
  await writeCursor(EMPTY_CURSOR);
}

// ── Transport ────────────────────────────────────────────────────────

/**
 * One API call, with a deadline.
 *
 * Every failure — no backend, a 500, a body that is not JSON, a device that
 * went into a tunnel — arrives here as a thrown error and is turned into a
 * failed {@link SyncResult} by the caller. None of them may reach a screen.
 */
async function callApi<T>(path: string, body?: unknown): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SYNC_TIMEOUT_MS);
  // The mirror routes are closed: `functions/api/_middleware.js` demands a token
  // that names an active `admin` or `manager` on every one of them. A device
  // that signed in against the directory has one; a device that fell back to the
  // local flow in a dead spot has not, and its pass simply fails and reports
  // `offline` — which is the same thing it does with no signal at all, and is
  // exactly the degradation the offline-first contract promises.
  const token = readAuthToken();
  try {
    const response = await fetch(syncEndpoint(APP_CONFIG.apiBaseUrl, path), {
      method: body === undefined ? 'GET' : 'POST',
      headers: {
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return (await response.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

// ── Push ─────────────────────────────────────────────────────────────

/** Everything written on this device since the last push, table by table. */
async function collectPush(cursor: SyncCursor): Promise<{
  tables: Record<string, SyncableRecord[]>;
  auditLogs: AuditLogEntry[];
  count: number;
}> {
  const tables: Record<string, SyncableRecord[]> = {};
  let count = 0;

  for (const table of SYNC_TABLES) {
    const rows = (await db.table(table).toArray()) as SyncableRecord[];
    const pending = pendingPush(rows, cursor.pushedThrough);
    if (pending.length > 0) {
      tables[table] = pending;
      count += pending.length;
    }
  }

  const ledger = await db.auditLogs.toArray();
  const auditLogs = ledger
    .filter((row) => row.createdAt > cursor.auditPushedThrough)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  return { tables, auditLogs, count: count + auditLogs.length };
}

// ── Pull ─────────────────────────────────────────────────────────────

interface PullResponse {
  tables?: Partial<Record<SyncTable, SyncableRecord[]>>;
  auditLogs?: AuditLogEntry[];
}

/**
 * Writes an incoming batch into Dexie, record by record, keeping whichever
 * version {@link shouldApplyRemote} picks. Returns how many actually landed —
 * a pull that only echoed back this device's own writes changes nothing, and
 * must not make the screens re-read.
 */
async function applyPull(
  payload: PullResponse,
): Promise<{ applied: number; audit: number; tables: SyncTable[] }> {
  let applied = 0;
  const tables: SyncTable[] = [];

  for (const table of SYNC_TABLES) {
    const incoming = payload.tables?.[table] ?? [];
    if (incoming.length === 0) continue;
    const store = db.table(table);
    const winners: SyncableRecord[] = [];
    for (const remote of incoming) {
      if (!remote?.id) continue;
      const local = (await store.get(remote.id)) as SyncableRecord | undefined;
      if (shouldApplyRemote(local, remote)) winners.push(remote);
    }
    if (winners.length > 0) {
      await store.bulkPut(winners);
      applied += winners.length;
      tables.push(table);
    }
  }

  // The ledger is append-only, so there is nothing to merge: a row this device
  // already holds is simply not written again.
  const rows = payload.auditLogs ?? [];
  let audit = 0;
  if (rows.length > 0) {
    const held = new Set((await db.auditLogs.toArray()).map((row) => row.id));
    const fresh = unseenAuditRows(rows, held);
    if (fresh.length > 0) {
      await db.auditLogs.bulkPut(fresh);
      audit = fresh.length;
    }
  }

  return { applied, audit, tables };
}

// ── One pass ─────────────────────────────────────────────────────────

/**
 * Push, then pull, then move the cursors — in that order, so a record written
 * on this device is already on the server before the server is asked what is
 * new, and the pass never pulls a stale version over a fresh local one.
 *
 * Never throws. A failure is a `SyncResult` with `ok: false`, because a shell
 * that has to catch its own background timer is a shell that will eventually
 * forget to.
 */
export async function syncOnce(): Promise<SyncResult> {
  try {
    const cursor = await readCursor();
    const outgoing = await collectPush(cursor);

    if (outgoing.count > 0) {
      await callApi('sync/push', { tables: outgoing.tables, auditLogs: outgoing.auditLogs });
    }

    const incoming = await callApi<PullResponse>(
      `sync/pull?since=${encodeURIComponent(cursor.pulledThrough)}` +
        `&auditSince=${encodeURIComponent(cursor.auditPulledThrough)}`,
    );
    const { applied, audit, tables } = await applyPull(incoming);

    // Somebody else's write just landed in Dexie, which no screen is watching.
    // Announcing it as a *remote* change repaints every hook that reads those
    // tables on the same frame — the workload graph, the rosters, the board —
    // without raising the push signal or re-broadcasting, because this device
    // did not write the record and does not owe anybody a copy of it.
    for (const domain of pulledDomains(tables, audit > 0)) {
      emitRemoteStoreChange(domain);
    }

    // Each cursor moves only as far as records were actually seen — never to
    // "now", which would step over a write still in flight from another device.
    const pulledRows = SYNC_TABLES.flatMap((table) => incoming.tables?.[table] ?? []);
    const pushedRows = Object.values(outgoing.tables).flat();
    await writeCursor({
      pushedThrough: advanceCursor(cursor.pushedThrough, pushedRows),
      pulledThrough: advanceCursor(cursor.pulledThrough, pulledRows),
      auditPushedThrough: outgoing.auditLogs.reduce(
        (newest, row) => (row.createdAt > newest ? row.createdAt : newest),
        cursor.auditPushedThrough,
      ),
      auditPulledThrough: (incoming.auditLogs ?? []).reduce(
        (newest, row) => (row.createdAt > newest ? row.createdAt : newest),
        cursor.auditPulledThrough,
      ),
    });

    return {
      ok: true,
      pushed: outgoing.count,
      pulled: applied,
      auditPulled: audit,
      changed: applied + audit > 0,
    };
  } catch (error) {
    return { ...EMPTY_RESULT, ok: false, error: (error as Error).message };
  }
}
